import { isValidUuid } from '@/util/is-valid-uuid'
import { z } from 'zod'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiWebSocketBaseUrl } from '@/constants/constants-links'
import {
  AgentV2UIState,
  AgentV2ClientMessageType,
  AgentV2ClientSurface,
  AgentV2RunStopMessage,
  AgentV2SearchDefaults,
  AgentV2ServerEventEnvelope,
  AgentV2ServerEventEnvelopeSchema,
  AgentV2ServerEventType,
  kAgentV2LongInputWarningChars,
  kAgentV2MaxTurnInputChars,
  AgentV2TurnStartMessage,
  MatterArtifactVersionSelection,
} from '@/agent-v2/store/schemas'
import { kAgentV2ChatBasePath } from '@/constants/constants-api-paths'

export { kAgentV2LongInputWarningChars, kAgentV2MaxTurnInputChars }

export function getAgentV2MatterSubmitBlockedReason(matterId: string | null | undefined): string | null {
  if (!isValidUuid(matterId)) {
    return 'A valid matter is required to start a chat.'
  }
  return null
}

export function buildAgentV2WebSocketUrl(conversationId: string): string {
  return `${kPaxtonAppApiWebSocketBaseUrl()}${kAgentV2ChatBasePath}/${conversationId}`
}

export function buildAgentV2TurnStartMessage(args: {
  uiState?: AgentV2UIState
  clientRequestId: string
  inputText: string
  matterId: string
  searchDefaults: AgentV2SearchDefaults
  clientSurface: AgentV2ClientSurface
  matterArtifactSelection?: MatterArtifactVersionSelection
  selectedSkillIds?: string[]
  selectedPaxtonSkillNames?: string[]
}): AgentV2TurnStartMessage {
  return {
    type: AgentV2ClientMessageType.TURN_START,
    payload: {
      client_request_id: args.clientRequestId,
      input_text: args.inputText,
      matter_id: args.matterId,
      search_defaults: args.searchDefaults,
      ...(args.selectedSkillIds?.length ? { selected_skill_ids: args.selectedSkillIds } : {}),
      ...(args.selectedPaxtonSkillNames?.length ? { selected_paxton_skill_names: args.selectedPaxtonSkillNames } : {}),
      client_surface: args.clientSurface,
      ...(args.matterArtifactSelection ? { matter_artifact_selection: args.matterArtifactSelection } : {}),
      ...(args.uiState ? { ui_state: args.uiState } : {}),
    },
  }
}

export function buildAgentV2RunStopMessage(): AgentV2RunStopMessage {
  return {
    type: AgentV2ClientMessageType.RUN_STOP,
    payload: {},
  }
}

export enum AgentV2SocketParseOutcome {
  EVENT = 'event',
  DROP = 'drop',
  FATAL = 'fatal',
}

export type AgentV2SocketParseResult =
  | { outcome: AgentV2SocketParseOutcome.EVENT; event: AgentV2ServerEventEnvelope }
  | { outcome: AgentV2SocketParseOutcome.DROP }
  | { outcome: AgentV2SocketParseOutcome.FATAL }

const kAgentV2SupportedEventVersion = 1
const kKnownAgentV2ServerEventTypes = new Set<string>(Object.values(AgentV2ServerEventType))
const AgentV2SocketEventOuterSchema = z.object({ type: z.string(), version: z.number() })

// Two-stage parse: unknown event types and unsupported versions are dropped with
// telemetry so the run continues; only genuinely malformed frames fail the run (ENG-7067)
export function parseAgentV2SocketEvent(event: MessageEvent): AgentV2SocketParseResult {
  let parsedJson: unknown
  try {
    parsedJson = JSON.parse(event.data)
  } catch {
    console.warn('Invalid Agent v2 WebSocket JSON')
    Sentry.captureMessage('Agent v2: WebSocket message is not valid JSON')
    return { outcome: AgentV2SocketParseOutcome.FATAL }
  }

  const outerEnvelope = AgentV2SocketEventOuterSchema.safeParse(parsedJson)
  if (!outerEnvelope.success) {
    console.warn('Agent v2 WebSocket event is missing type/version', outerEnvelope.error)
    Sentry.captureMessage('Agent v2: WebSocket event is missing type/version')
    return { outcome: AgentV2SocketParseOutcome.FATAL }
  }

  const { type, version } = outerEnvelope.data
  if (!kKnownAgentV2ServerEventTypes.has(type) || version !== kAgentV2SupportedEventVersion) {
    console.warn('Dropped unrecognized Agent v2 WebSocket event', type, version)
    Sentry.captureMessage('Agent v2: dropped unrecognized WebSocket event', { extra: { eventType: type, version } })
    return { outcome: AgentV2SocketParseOutcome.DROP }
  }

  const parsedEvent = AgentV2ServerEventEnvelopeSchema.safeParse(parsedJson)
  if (!parsedEvent.success) {
    console.warn('Invalid Agent v2 WebSocket event', parsedEvent.error)
    Sentry.captureException(parsedEvent.error, { extra: { eventType: type } })
    return { outcome: AgentV2SocketParseOutcome.FATAL }
  }

  return { outcome: AgentV2SocketParseOutcome.EVENT, event: parsedEvent.data }
}
